import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, 
  Folder, 
  Plus, 
  FileText, 
  MessageSquare, 
  Globe, 
  StickyNote, 
  Settings,
  Trash2,
  Edit2,
  ChevronRight,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { useAppearance } from '../contexts/AppearanceContext';
import { 
  getContextSpaces, 
  createContextSpace, 
  deleteContextSpace,
  CONTEXT_SPACE_ICONS,
  CONTEXT_SPACE_COLORS 
} from '../services/contextSpacesApi';

const ContextSpacesPage = ({ onBack, onSelectContextSpace }) => {
  const [contextSpaces, setContextSpaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newSpaceName, setNewSpaceName] = useState('');
  const [newSpaceDescription, setNewSpaceDescription] = useState('');
  const [newSpaceIcon, setNewSpaceIcon] = useState('📁');
  const [creating, setCreating] = useState(false);
  const { isSuppleMode } = useAppearance();

  useEffect(() => {
    loadContextSpaces();
  }, []);

  const loadContextSpaces = async () => {
    try {
      setLoading(true);
      const spaces = await getContextSpaces();
      setContextSpaces(spaces);
      setError(null);
    } catch (err) {
      console.error('Failed to load context spaces:', err);
      setError('Failed to load context spaces');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSpace = async () => {
    if (!newSpaceName.trim()) return;

    try {
      setCreating(true);
      await createContextSpace({
        name: newSpaceName.trim(),
        description: newSpaceDescription.trim(),
        icon: newSpaceIcon
      });
      
      // Reload spaces and close form
      await loadContextSpaces();
      setShowCreateForm(false);
      setNewSpaceName('');
      setNewSpaceDescription('');
      setNewSpaceIcon('📁');
    } catch (err) {
      console.error('Failed to create context space:', err);
      alert('Failed to create context space');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteSpace = async (spaceId) => {
    if (!window.confirm('Are you sure you want to delete this Context Space? This will remove all associated resources.')) {
      return;
    }

    try {
      await deleteContextSpace(spaceId);
      await loadContextSpaces();
    } catch (err) {
      console.error('Failed to delete context space:', err);
      alert('Failed to delete context space');
    }
  };

  const getResourceTypeIcon = (type) => {
    switch (type) {
      case 'URL':
        return <Globe className="w-3 h-3" />;
      case 'FILE':
        return <FileText className="w-3 h-3" />;
      case 'NOTE':
      case 'TEXT':
        return <StickyNote className="w-3 h-3" />;
      default:
        return <FileText className="w-3 h-3" />;
    }
  };

  if (loading) {
    return (
      <div className={`h-screen flex flex-col ${
        isSuppleMode ? 'supple-bg-primary' : 'bg-gray-50'
      }`}>
        {/* Header */}
        <div className={`flex items-center gap-4 p-6 border-b ${
          isSuppleMode ? 'supple-border-subtle supple-bg-secondary' : 'border-gray-200 bg-white'
        }`}>
          <button
            onClick={onBack}
            className={`p-2 rounded-lg transition-colors ${
              isSuppleMode 
                ? 'hover:supple-card-glass-hover supple-text-primary'
                : 'hover:bg-gray-100 text-gray-600'
            }`}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className={`p-2 rounded-lg ${
            isSuppleMode ? 'bg-purple-500/20' : 'bg-blue-100'
          }`}>
            <Folder className={`w-6 h-6 ${
              isSuppleMode ? 'text-purple-300' : 'text-blue-600'
            }`} />
          </div>
          <div>
            <h1 className={`text-2xl font-bold ${
              isSuppleMode ? 'supple-text-primary' : 'text-gray-900'
            }`}>
              Context Spaces
            </h1>
            <p className={`text-sm ${
              isSuppleMode ? 'supple-text-muted' : 'text-gray-500'
            }`}>
              Loading your spaces...
            </p>
          </div>
        </div>

        {/* Loading Content */}
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className={`w-8 h-8 animate-spin ${
            isSuppleMode ? 'text-purple-300' : 'text-blue-600'
          }`} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`h-screen flex flex-col ${
        isSuppleMode ? 'supple-bg-primary' : 'bg-gray-50'
      }`}>
        {/* Header */}
        <div className={`flex items-center gap-4 p-6 border-b ${
          isSuppleMode ? 'supple-border-subtle supple-bg-secondary' : 'border-gray-200 bg-white'
        }`}>
          <button
            onClick={onBack}
            className={`p-2 rounded-lg transition-colors ${
              isSuppleMode 
                ? 'hover:supple-card-glass-hover supple-text-primary'
                : 'hover:bg-gray-100 text-gray-600'
            }`}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className={`p-2 rounded-lg ${
            isSuppleMode ? 'bg-purple-500/20' : 'bg-blue-100'
          }`}>
            <Folder className={`w-6 h-6 ${
              isSuppleMode ? 'text-purple-300' : 'text-blue-600'
            }`} />
          </div>
          <div>
            <h1 className={`text-2xl font-bold ${
              isSuppleMode ? 'supple-text-primary' : 'text-gray-900'
            }`}>
              Context Spaces
            </h1>
          </div>
        </div>

        {/* Error Content */}
        <div className="flex-1 flex flex-col items-center justify-center p-8">
          <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
          <p className="text-red-500 text-center">{error}</p>
          <button
            onClick={loadContextSpaces}
            className={`mt-4 px-4 py-2 rounded-lg border transition-colors ${
              isSuppleMode
                ? 'border-purple-300/30 text-purple-300 hover:bg-purple-500/10'
                : 'border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`h-screen flex flex-col ${
      isSuppleMode ? 'supple-bg-primary' : 'bg-gray-50'
    }`}>
      {/* Header */}
      <div className={`flex items-center justify-between p-6 border-b ${
        isSuppleMode ? 'supple-border-subtle supple-bg-secondary' : 'border-gray-200 bg-white'
      }`}>
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className={`p-2 rounded-lg transition-colors ${
              isSuppleMode 
                ? 'hover:supple-card-glass-hover supple-text-primary'
                : 'hover:bg-gray-100 text-gray-600'
            }`}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className={`p-2 rounded-lg ${
            isSuppleMode ? 'bg-purple-500/20' : 'bg-blue-100'
          }`}>
            <Folder className={`w-6 h-6 ${
              isSuppleMode ? 'text-purple-300' : 'text-blue-600'
            }`} />
          </div>
          <div>
            <h1 className={`text-2xl font-bold ${
              isSuppleMode ? 'supple-text-primary' : 'text-gray-900'
            }`}>
              Context Spaces
            </h1>
            <p className={`text-sm ${
              isSuppleMode ? 'supple-text-muted' : 'text-gray-500'
            }`}>
              Organize your resources and conversations
            </p>
          </div>
        </div>

        <button
          onClick={() => setShowCreateForm(true)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
            isSuppleMode
              ? 'bg-purple-500/20 text-purple-300 hover:bg-purple-500/30'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          <Plus className="w-4 h-4" />
          Create Space
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {showCreateForm && (
          <div className={`mb-6 p-6 rounded-xl border ${
            isSuppleMode 
              ? 'supple-card-glass border-purple-300/30' 
              : 'bg-white border-gray-200'
          } shadow-sm`}>
            <h3 className={`text-lg font-semibold mb-4 ${
              isSuppleMode ? 'supple-text-primary' : 'text-gray-900'
            }`}>
              Create New Context Space
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  isSuppleMode ? 'supple-text-primary' : 'text-gray-700'
                }`}>
                  Name
                </label>
                <input
                  type="text"
                  value={newSpaceName}
                  onChange={(e) => setNewSpaceName(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg ${
                    isSuppleMode
                      ? 'supple-input border-purple-300/30'
                      : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                  }`}
                  placeholder="Enter space name..."
                />
              </div>
              
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  isSuppleMode ? 'supple-text-primary' : 'text-gray-700'
                }`}>
                  Description (optional)
                </label>
                <textarea
                  value={newSpaceDescription}
                  onChange={(e) => setNewSpaceDescription(e.target.value)}
                  rows={2}
                  className={`w-full px-3 py-2 border rounded-lg ${
                    isSuppleMode
                      ? 'supple-input border-purple-300/30'
                      : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                  }`}
                  placeholder="Describe this space..."
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  isSuppleMode ? 'supple-text-primary' : 'text-gray-700'
                }`}>
                  Icon
                </label>
                <div className="flex flex-wrap gap-2">
                  {CONTEXT_SPACE_ICONS.map((icon) => (
                    <button
                      key={icon}
                      onClick={() => setNewSpaceIcon(icon)}
                      className={`p-2 rounded-lg transition-colors ${
                        newSpaceIcon === icon
                          ? isSuppleMode
                            ? 'bg-purple-500/30 border border-purple-300'
                            : 'bg-blue-100 border border-blue-300'
                          : isSuppleMode
                            ? 'hover:bg-purple-500/10 border border-transparent'
                            : 'hover:bg-gray-100 border border-transparent'
                      }`}
                    >
                      <span className="text-xl">{icon}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleCreateSpace}
                  disabled={!newSpaceName.trim() || creating}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors disabled:opacity-50 ${
                    isSuppleMode
                      ? 'bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 disabled:hover:bg-purple-500/20'
                      : 'bg-blue-600 text-white hover:bg-blue-700 disabled:hover:bg-blue-600'
                  }`}
                >
                  {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  {creating ? 'Creating...' : 'Create'}
                </button>
                <button
                  onClick={() => setShowCreateForm(false)}
                  className={`px-4 py-2 rounded-lg border transition-colors ${
                    isSuppleMode
                      ? 'border-purple-300/30 text-purple-300 hover:bg-purple-500/10'
                      : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {contextSpaces.length === 0 && !showCreateForm ? (
          <div className="text-center py-12">
            <Folder className={`w-16 h-16 mx-auto mb-4 ${
              isSuppleMode ? 'text-purple-300/50' : 'text-gray-400'
            }`} />
            <h3 className={`text-xl font-semibold mb-2 ${
              isSuppleMode ? 'supple-text-primary' : 'text-gray-900'
            }`}>
              No Context Spaces Yet
            </h3>
            <p className={`text-sm mb-6 ${
              isSuppleMode ? 'supple-text-muted' : 'text-gray-500'
            }`}>
              Create your first context space to organize resources and conversations
            </p>
            <button
              onClick={() => setShowCreateForm(true)}
              className={`flex items-center gap-2 mx-auto px-6 py-3 rounded-lg transition-colors ${
                isSuppleMode
                  ? 'bg-purple-500/20 text-purple-300 hover:bg-purple-500/30'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              <Plus className="w-4 h-4" />
              Create Your First Space
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {contextSpaces.map((space) => (
              <div
                key={space.id}
                className={`p-6 rounded-xl border transition-all ${
                  isSuppleMode 
                    ? 'supple-card-glass border-purple-300/30 hover:supple-card-glass-hover' 
                    : 'bg-white border-gray-200 hover:shadow-md'
                } shadow-sm cursor-pointer`}
                onClick={() => onSelectContextSpace(space)}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{space.icon}</span>
                    <div>
                      <h3 className={`font-semibold ${
                        isSuppleMode ? 'supple-text-primary' : 'text-gray-900'
                      }`}>
                        {space.name}
                      </h3>
                      {space.description && (
                        <p className={`text-sm mt-1 ${
                          isSuppleMode ? 'supple-text-muted' : 'text-gray-500'
                        }`}>
                          {space.description}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex gap-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        // TODO: Add edit functionality
                      }}
                      className={`p-1 rounded transition-colors ${
                        isSuppleMode
                          ? 'hover:bg-purple-500/20 text-purple-300'
                          : 'hover:bg-gray-100 text-gray-400'
                      }`}
                    >
                      <Edit2 className="w-3 h-3" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteSpace(space.id);
                      }}
                      className={`p-1 rounded transition-colors ${
                        isSuppleMode
                          ? 'hover:bg-red-500/20 text-red-300'
                          : 'hover:bg-red-50 text-red-400'
                      }`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>

                <div className="space-y-3">
                  {/* Resources */}
                  {space.resources && space.resources.length > 0 && (
                    <div className="flex items-center gap-2">
                      {getResourceTypeIcon(space.resources[0]?.type)}
                      <span className={`text-sm ${
                        isSuppleMode ? 'supple-text-muted' : 'text-gray-600'
                      }`}>
                        {space.resources.length} resource{space.resources.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  )}
                  
                  {/* Conversations */}
                  {space.conversations && space.conversations.length > 0 && (
                    <div className="flex items-center gap-2">
                      <MessageSquare className="w-3 h-3" />
                      <span className={`text-sm ${
                        isSuppleMode ? 'supple-text-muted' : 'text-gray-600'
                      }`}>
                        {space.conversations.length} conversation{space.conversations.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-3">
                    <span className={`text-xs ${
                      isSuppleMode ? 'supple-text-muted' : 'text-gray-500'
                    }`}>
                      Click to open
                    </span>
                    <ChevronRight className={`w-4 h-4 ${
                      isSuppleMode ? 'supple-text-muted' : 'text-gray-400'
                    }`} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ContextSpacesPage;